import { useEffect, useMemo, useState } from "react";
import { Plus, Sparkles } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useToast } from "@/hooks/use-toast";
import { formatMoney } from "@/components/clinic-health-calculator/ClinicHealthCalculator";
import { parsePesoToCentavos, centavosToPesoInput } from "@/lib/money";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

// Render "1st", "2nd", "3rd", "21st"… for the day-of-month label.
function ordinal(n) {
  const day = Number(n);
  if (!Number.isFinite(day)) return String(n);
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

function currentMonthKey() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

const EMPTY_FORM = {
  categoryId: "",
  label: "",
  amount: "",
  dayOfMonth: "1",
  branchId: "",
  vendorName: "",
  endsMonth: "",
  postCurrentMonth: false,
};

const inputClass =
  "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-[14px] text-slate-950 outline-none transition focus:border-teal-600 focus:ring-2 focus:ring-teal-100";

export default function RecurringSection({
  recurring,
  categories,
  branches,
  suggestions,
  currency,
  onChanged,
}) {
  const { toast } = useToast();

  const templates = Array.isArray(recurring) ? recurring : [];
  const categoryList = Array.isArray(categories) ? categories : [];
  const branchList = Array.isArray(branches) ? branches : [];
  const suggestionList = Array.isArray(suggestions) ? suggestions : [];

  // First-visit suggestions only appear when there are no templates yet.
  const showSuggestions = templates.length === 0 && suggestionList.length > 0;
  const showEmptyState = templates.length === 0 && suggestionList.length === 0;

  // Per-row in-flight tracking for the Active toggle.
  const [togglingId, setTogglingId] = useState(null);

  // Drawer state. `editing` holds the template being edited (null = create).
  // `seed` carries optional pre-fill values from a suggestion.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [seed, setSeed] = useState(null);

  const openCreate = (prefill = null) => {
    setEditing(null);
    setSeed(prefill);
    setDrawerOpen(true);
  };

  const openEdit = (template) => {
    setEditing(template);
    setSeed(null);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
    setSeed(null);
  };

  const handleToggleActive = async (template) => {
    setTogglingId(template.id);
    try {
      const { error: rpcError } = await supabase.rpc("upsert_expense_recurring", {
        p_input: { id: template.id, active: !template.active },
      });
      if (rpcError) {
        toast({
          title: "Couldn't update the recurring expense",
          description: rpcError.message || "Something went wrong. Try again.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: template.active ? "Recurring expense paused" : "Recurring expense resumed",
        description: template.active
          ? "It won't post again until you turn it back on."
          : "It will post automatically going forward.",
      });
      await onChanged?.();
    } catch (err) {
      toast({
        title: "Couldn't update the recurring expense",
        description: err?.message || "Something went wrong. Try again.",
        variant: "destructive",
      });
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <section data-testid="expenses-recurring" className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="text-[15px] font-semibold text-slate-900">Recurring expenses</h2>
            <p className="mt-0.5 text-[12px] text-slate-500">
              Fixed monthly costs that post to your ledger automatically.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            data-testid="recurring-add-button"
            onClick={() => openCreate(null)}
          >
            <Plus className="mr-1.5 h-4 w-4" strokeWidth={2.5} />
            Add recurring
          </Button>
        </div>

        {showSuggestions ? (
          <div
            data-testid="recurring-suggestions"
            className="border-b border-slate-200 bg-teal-50/60 px-4 py-4"
          >
            <div className="flex items-start gap-2">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" strokeWidth={2.25} />
              <div className="min-w-0 flex-1">
                <h3 className="text-[13px] font-semibold text-slate-900">
                  Set up recurring expenses from your last overhead month
                </h3>
                <p className="mt-0.5 text-[12px] text-slate-600">
                  We found these fixed costs in your most recent overhead. Review each one before adding it.
                </p>
                <ul className="mt-3 space-y-2">
                  {suggestionList.map((suggestion, index) => (
                    <li
                      key={`${suggestion.category_id}-${suggestion.label}-${index}`}
                      className="flex items-center justify-between gap-3 rounded-md border border-teal-200 bg-white px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-[13px] font-medium text-slate-800">
                          {suggestion.label}
                        </div>
                        <div className="text-[12px] tabular-nums text-slate-500">
                          {formatMoney(suggestion.amount_centavos, currency)}
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          openCreate({
                            categoryId: suggestion.category_id || "",
                            label: suggestion.label || "",
                            amount: centavosToPesoInput(suggestion.amount_centavos),
                            dayOfMonth: "1",
                          })
                        }
                      >
                        Create
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ) : null}

        {templates.length > 0 ? (
          <ul className="divide-y divide-slate-100">
            {templates.map((template) => {
              const category = template.category || {};
              const isToggling = togglingId === template.id;
              return (
                <li
                  key={template.id}
                  data-testid="recurring-row"
                  className="flex items-center justify-between gap-4 px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-[14px] font-medium text-slate-900">
                        {template.label}
                      </span>
                      <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                        {category.label || "—"}
                      </span>
                    </div>
                    <div className="mt-0.5 text-[12px] text-slate-500">
                      <span className="font-semibold tabular-nums text-slate-700">
                        {formatMoney(template.amount_centavos, currency)}
                      </span>
                      {" · "}
                      <span>posts on the {ordinal(template.day_of_month)}</span>
                      {" · "}
                      <span>{template.branch_name || "Clinic-wide"}</span>
                      {template.staff_name ? (
                        <>
                          {" · "}
                          <span>{template.staff_name}</span>
                        </>
                      ) : null}
                      {template.vendor_name ? (
                        <>
                          {" · "}
                          <span>{template.vendor_name}</span>
                        </>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-4">
                    <label className="flex items-center gap-2 text-[12px] font-medium text-slate-600">
                      <Switch
                        checked={!!template.active}
                        disabled={isToggling}
                        onCheckedChange={() => handleToggleActive(template)}
                        aria-label={template.active ? "Pause recurring expense" : "Resume recurring expense"}
                      />
                      <span>{template.active ? "Active" : "Paused"}</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => openEdit(template)}
                      className="text-[12px] font-semibold text-teal-600 transition hover:text-teal-700"
                    >
                      Edit
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}

        {showEmptyState ? (
          <div className="px-4 py-8 text-center text-[13px] text-slate-500">
            No recurring expenses yet.
          </div>
        ) : null}
      </div>

      <RecurringDrawer
        open={drawerOpen}
        editing={editing}
        seed={seed}
        categories={categoryList}
        branches={branchList}
        onClose={closeDrawer}
        onSaved={async () => {
          closeDrawer();
          await onChanged?.();
        }}
      />
    </section>
  );
}

function RecurringDrawer({ open, editing, seed, categories, branches, onClose, onSaved }) {
  const { toast } = useToast();
  const isEdit = !!editing;

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  // Reset the form each time the drawer opens, seeded from the template being
  // edited or the suggestion prefill, otherwise a clean slate.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        categoryId: editing.category?.id || "",
        label: editing.label || "",
        amount: centavosToPesoInput(editing.amount_centavos),
        dayOfMonth: String(editing.day_of_month ?? "1"),
        branchId: editing.branch_id || "",
        vendorName: editing.vendor_name || "",
        endsMonth: editing.ends_month ? String(editing.ends_month).slice(0, 7) : "",
        postCurrentMonth: false,
      });
    } else {
      setForm({ ...EMPTY_FORM, ...(seed || {}) });
    }
    setSubmitting(false);
  }, [open, editing, seed]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const amountCentavos = useMemo(() => parsePesoToCentavos(form.amount), [form.amount]);
  const dayNum = Number.parseInt(form.dayOfMonth, 10);
  const dayValid = Number.isFinite(dayNum) && dayNum >= 1 && dayNum <= 31;
  const labelValid = form.label.trim().length >= 1 && form.label.trim().length <= 120;
  const canSubmit =
    !!form.categoryId && amountCentavos > 0 && dayValid && labelValid && !submitting;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      const payload = {
        category_id: form.categoryId,
        label: form.label.trim(),
        amount_centavos: amountCentavos,
        day_of_month: dayNum,
        branch_id: form.branchId || null,
        vendor_name: form.vendorName.trim() || null,
        ends_month: form.endsMonth || null,
        active: true,
      };
      if (isEdit) {
        payload.id = editing.id;
        // Preserve the template's current active state on edit (don't flip it).
        payload.active = !!editing.active;
      } else {
        payload.starts_month = currentMonthKey();
        payload.post_current_month = !!form.postCurrentMonth;
      }

      const { data: result, error: rpcError } = await supabase.rpc("upsert_expense_recurring", {
        p_input: payload,
      });

      if (rpcError) {
        toast({
          title: isEdit ? "Couldn't save changes" : "Couldn't add the recurring expense",
          description: rpcError.message || "Something went wrong. Try again.",
          variant: "destructive",
        });
        setSubmitting(false);
        return;
      }

      const postedNow = !!result?.posted_now;
      toast({
        title: isEdit ? "Recurring expense updated" : "Recurring expense added",
        description: postedNow
          ? "Posted this month too."
          : isEdit
            ? "Your changes have been saved."
            : "It will post automatically each month.",
      });
      await onSaved?.();
    } catch (err) {
      toast({
        title: isEdit ? "Couldn't save changes" : "Couldn't add the recurring expense",
        description: err?.message || "Something went wrong. Try again.",
        variant: "destructive",
      });
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next && !submitting) onClose?.(); }}>
      <SheetContent side="right" className="flex w-full max-w-[440px] flex-col gap-0 p-0 sm:max-w-[440px]">
        <SheetHeader className="border-b border-slate-200 px-5 py-4 text-left">
          <SheetTitle>{isEdit ? "Edit recurring expense" : "Add recurring expense"}</SheetTitle>
          <SheetDescription>
            {isEdit
              ? "Update this fixed monthly cost."
              : "Set a fixed monthly cost that posts to your ledger automatically."}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <Field label="Label" required>
              <Input
                type="text"
                value={form.label}
                onChange={(event) => setField("label", event.target.value)}
                placeholder="e.g. Office rent"
                maxLength={120}
                className="h-10"
              />
            </Field>

            <Field label="Category" required>
              <select
                value={form.categoryId}
                onChange={(event) => setField("categoryId", event.target.value)}
                className={inputClass}
                required
              >
                <option value="">Select a category…</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Amount" required>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] font-semibold text-slate-400">
                  ₱
                </span>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={form.amount}
                  onChange={(event) => setField("amount", event.target.value)}
                  placeholder="0.00"
                  className="h-10 pl-7"
                />
              </div>
            </Field>

            <Field label="Day of month" required>
              <Input
                type="number"
                min={1}
                max={31}
                value={form.dayOfMonth}
                onChange={(event) => setField("dayOfMonth", event.target.value)}
                className="h-10"
              />
              <p className="text-[11px] text-slate-400">
                Posts on this day each month (1–31).
              </p>
            </Field>

            <Field label="Branch">
              <select
                value={form.branchId}
                onChange={(event) => setField("branchId", event.target.value)}
                className={inputClass}
              >
                <option value="">Clinic-wide</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Vendor">
              <Input
                type="text"
                value={form.vendorName}
                onChange={(event) => setField("vendorName", event.target.value)}
                placeholder="Who gets paid"
                className="h-10"
              />
            </Field>

            <Field label="End month">
              <Input
                type="month"
                value={form.endsMonth}
                onChange={(event) => setField("endsMonth", event.target.value)}
                className="h-10"
              />
              <p className="text-[11px] text-slate-400">
                Optional. Leave blank to keep posting until you pause it.
              </p>
            </Field>

            {!isEdit ? (
              <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={form.postCurrentMonth}
                  onChange={(event) => setField("postCurrentMonth", event.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-[12px] text-slate-600">
                  <span className="font-semibold text-slate-700">Also post for this month.</span>{" "}
                  Adds this month's entry to your ledger now, in addition to scheduling it going forward.
                </span>
              </label>
            ) : null}
          </div>

          <SheetFooter className="border-t border-slate-200 px-5 py-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {submitting
                ? "Saving…"
                : isEdit
                  ? "Save changes"
                  : "Add recurring"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[12px] font-semibold text-slate-600">
        {label}
        {required ? <span className="ml-0.5 text-rose-500">*</span> : null}
      </label>
      {children}
    </div>
  );
}
